import type { RawData, TestResult, ValidationResult } from '../types.ts';
import { invalid, valid } from '../validation.ts';

export type SerializedNumber = number | 'NaN' | 'Infinity' | '-Infinity' | '-0';

export function isRecord(value: unknown): value is RawData {
  return value != null && typeof value == 'object' && !Array.isArray(value);
}

export function hasError(rawData: RawData): string | null {
  return typeof rawData.error == 'string' ? rawData.error : null;
}

export function result(
  rawData: RawData,
  format: (rawData: RawData) => string,
): TestResult {
  const value = format(rawData);
  if (value.includes('\n') || [...value].length > 32) {
    throw new Error('Formatted table value exceeds the 32-character limit');
  }
  return { rawData, value };
}

export function formatError(rawData: RawData): string | null {
  const error = hasError(rawData);
  return error ? 'Test error' : null;
}

export function validationError(rawData: RawData): ValidationResult | null {
  const error = hasError(rawData);
  return error ? invalid(error) : null;
}

export function validation(reasons: string[]): ValidationResult {
  return reasons.length == 0 ? valid() : invalid(reasons);
}

export function serializeNumber(value: number): SerializedNumber;
export function serializeNumber(
  value: number | null | undefined,
): SerializedNumber | null;
export function serializeNumber(
  value: number | null | undefined,
): SerializedNumber | null {
  if (value == null) {
    return null;
  }
  if (Number.isNaN(value)) {
    return 'NaN';
  }
  if (value == Number.POSITIVE_INFINITY) {
    return 'Infinity';
  }
  if (value == Number.NEGATIVE_INFINITY) {
    return '-Infinity';
  }
  if (Object.is(value, -0)) {
    return '-0';
  }
  return value;
}

export function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJson).join(',')}]`;
  }
  if (value != null && typeof value == 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function rotateLeft(value: number, amount: number): number {
  return ((value << amount) | (value >>> (32 - amount))) >>> 0;
}

export function md5sum(value: string): string {
  const input = new TextEncoder().encode(value);
  const paddedLength = Math.ceil((input.length + 9) / 64) * 64;
  const bytes = new Uint8Array(paddedLength);
  bytes.set(input);
  bytes[input.length] = 0x80;
  const bitLength = BigInt(input.length) * 8n;
  for (let index = 0; index < 8; index += 1) {
    bytes[paddedLength - 8 + index] = Number(
      (bitLength >> BigInt(index * 8)) & 0xffn,
    );
  }

  const shifts = [
    7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5,
    9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11,
    16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10,
    15, 21,
  ];
  const constants = Array.from(
    { length: 64 },
    (_value, index) =>
      Math.floor(Math.abs(Math.sin(index + 1)) * 0x100000000) >>> 0,
  );
  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  for (let offset = 0; offset < bytes.length; offset += 64) {
    const words = new Uint32Array(16);
    for (let index = 0; index < 16; index += 1) {
      const position = offset + index * 4;
      words[index] =
        (bytes[position] ?? 0) |
        ((bytes[position + 1] ?? 0) << 8) |
        ((bytes[position + 2] ?? 0) << 16) |
        ((bytes[position + 3] ?? 0) << 24);
    }
    let a = a0;
    let b = b0;
    let c = c0;
    let d = d0;
    for (let index = 0; index < 64; index += 1) {
      let mixed: number;
      let wordIndex: number;
      if (index < 16) {
        mixed = (b & c) | (~b & d);
        wordIndex = index;
      } else if (index < 32) {
        mixed = (d & b) | (~d & c);
        wordIndex = (5 * index + 1) % 16;
      } else if (index < 48) {
        mixed = b ^ c ^ d;
        wordIndex = (3 * index + 5) % 16;
      } else {
        mixed = c ^ (b | ~d);
        wordIndex = (7 * index) % 16;
      }
      const previousD = d;
      d = c;
      c = b;
      b =
        (b +
          rotateLeft(
            (a + mixed + (constants[index] ?? 0) + (words[wordIndex] ?? 0)) >>>
              0,
            shifts[index] ?? 0,
          )) >>>
        0;
      a = previousD;
    }
    a0 = (a0 + a) >>> 0;
    b0 = (b0 + b) >>> 0;
    c0 = (c0 + c) >>> 0;
    d0 = (d0 + d) >>> 0;
  }

  return [a0, b0, c0, d0]
    .flatMap((word) => [0, 8, 16, 24].map((shift) => (word >>> shift) & 0xff))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function hash(value: unknown): Promise<string> {
  return md5sum(stableJson(value));
}

export function numberValue(rawData: RawData, key: string): number | null {
  const value = rawData[key];
  return typeof value == 'number' && Number.isFinite(value) ? value : null;
}

export function stringValue(rawData: RawData, key: string): string | null {
  const value = rawData[key];
  return typeof value == 'string' ? value : null;
}

export function formatHash(rawData: RawData): string {
  return md5sum(stableJson(rawData));
}

export async function workerValue(expression: string): Promise<unknown> {
  const source = `self.postMessage(${expression})`;
  const url = URL.createObjectURL(
    new Blob([source], { type: 'text/javascript' }),
  );
  try {
    return await new Promise<unknown>((resolve, reject) => {
      const worker = new Worker(url);
      const timeout = window.setTimeout(() => {
        worker.terminate();
        reject(new Error('Dedicated Worker timed out'));
      }, 3000);
      worker.addEventListener('message', (event: MessageEvent<unknown>) => {
        window.clearTimeout(timeout);
        worker.terminate();
        resolve(event.data);
      });
      worker.addEventListener('error', (event) => {
        window.clearTimeout(timeout);
        worker.terminate();
        reject(new Error(event.message));
      });
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function withTimeout<T>(
  promise: Promise<T>,
  milliseconds: number,
  message: string,
): Promise<T> {
  return await Promise.race([
    promise,
    new Promise<never>((_resolve, reject) => {
      window.setTimeout(() => {
        reject(new Error(message));
      }, milliseconds);
    }),
  ]);
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  milliseconds: number,
  message: string,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => {
    controller.abort();
  }, milliseconds);
  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error(message, { cause: error });
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

export function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function domError(error: unknown): {
  name: string;
  message: string;
} {
  return error instanceof DOMException || error instanceof Error
    ? { name: error.name, message: error.message }
    : { name: 'Error', message: String(error) };
}
